import { AuiIf, ComposerPrimitive, useAui, useAuiState } from '@assistant-ui/react';
import { FeatureFlag } from '@utils/featureFlags';
import { Loader2Icon, MicIcon, SquareIcon } from 'lucide-react';
import { useEffect, useRef, useState, type FC } from 'react';
import { useShallow } from 'zustand/shallow';
import { TooltipIconButton } from '~/components/assistant-ui/TooltipIconButton';
import { Button } from '~/components/shadcn/Button';
import { useUserContext } from '~/react_context/UserContext';
import { useDictationStore } from '~/stores/dictation.store';
import { MAX_RECORDING_MS, TranscriptionError, transcribeRecording } from '~/utils/assistant-ui/openAiDictationAdapter';

export const useVoiceInputEnabled = () => {
	const { enableGlazierChat, featureFlags } = useUserContext();
	return enableGlazierChat && featureFlags.includes(FeatureFlag.CLARITY_VOICE);
};

const formatElapsed = (ms: number) => {
	const seconds = Math.floor(ms / 1000);
	return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

const useElapsed = (startedAt: number | undefined) => {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		if (startedAt === undefined) return;
		setNow(Date.now());
		const interval = setInterval(() => setNow(Date.now()), 250);
		return () => clearInterval(interval);
	}, [startedAt]);
	return startedAt === undefined ? 0 : Math.max(0, now - startedAt);
};

export const ComposerDictateButton: FC = () => {
	const phase = useDictationStore((s) => s.phase);

	if (phase.type === 'transcribing') {
		return (
			<Button type="button" variant="ghost" size="icon" className="size-8 rounded-full" disabled aria-label="Transcribing">
				<Loader2Icon className="size-4 animate-spin" />
			</Button>
		);
	}

	// Dictate and StopDictation render disabled, not hidden, when unavailable, so each needs its own condition.
	return (
		<>
			{/* @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node" */}
			<AuiIf condition={(s) => s.thread.capabilities.dictation && !s.thread.isRunning && s.composer.dictation == null}>
				<ComposerPrimitive.Dictate asChild>
					<TooltipIconButton
						tooltip="Voice input"
						side="bottom"
						type="button"
						className="size-8 rounded-full p-2"
						aria-label="Start voice input"
					>
						<MicIcon className="size-4" />
					</TooltipIconButton>
				</ComposerPrimitive.Dictate>
			</AuiIf>
			{/* @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node" */}
			<AuiIf condition={(s) => s.composer.dictation != null}>
				<ComposerPrimitive.StopDictation asChild>
					<TooltipIconButton
						tooltip="Stop and transcribe"
						side="bottom"
						type="button"
						className="size-8 rounded-full bg-red-500 p-2 text-white hover:bg-red-600 hover:text-white"
						aria-label="Stop voice input"
					>
						<SquareIcon className="size-3 fill-current" />
					</TooltipIconButton>
				</ComposerPrimitive.StopDictation>
			</AuiIf>
		</>
	);
};

export const ComposerDictationStatus: FC = () => {
	const aui = useAui();
	const { clearError, error, fail, failedRecording, phase, setPhase } = useDictationStore(
		useShallow((s) => ({
			clearError: s.clearError,
			error: s.error,
			fail: s.fail,
			failedRecording: s.failedRecording,
			phase: s.phase,
			setPhase: s.setPhase,
		}))
	);
	const elapsed = useElapsed(phase.type === 'recording' ? phase.startedAt : undefined);
	// @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node"
	const threadId: string | undefined = useAuiState((s) => s.threadListItem.id);
	const threadIdRef = useRef(threadId);

	// The store is shared by every thread, so a failed clip must not follow the user into another conversation.
	useEffect(() => {
		if (threadIdRef.current === threadId) return;
		threadIdRef.current = threadId;
		clearError();
	}, [clearError, threadId]);

	// The dictation session is already over, so a retried transcript can't go through it; append the way it would have.
	const retry = async (recording: Blob) => {
		const startedInThreadId = threadId;
		setPhase({ type: 'transcribing' });
		try {
			const transcript = await transcribeRecording(recording);
			setPhase({ type: 'idle' });
			if (threadIdRef.current !== startedInThreadId) return;
			if (!transcript) {
				fail("Didn't catch that. Try again, a little closer to the microphone.");
				return;
			}
			// @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node"
			const composer = aui.composer();
			const text: string = composer.getState().text;
			composer.setText(text && !text.endsWith(' ') ? `${text} ${transcript}` : `${text}${transcript}`);
		} catch (retryError) {
			const retryable = !(retryError instanceof TranscriptionError) || retryError.retryable;
			fail(retryError instanceof Error ? retryError.message : "Couldn't transcribe your recording.", retryable ? recording : undefined);
		}
	};

	if (phase.type === 'recording') {
		return (
			<div className="flex items-center gap-2 px-4 pt-2 text-xs text-muted-foreground" role="status">
				<span className="size-2 animate-pulse rounded-full bg-red-500" />
				Listening… {formatElapsed(elapsed)} / {formatElapsed(MAX_RECORDING_MS)}
			</div>
		);
	}

	if (phase.type === 'transcribing') {
		return (
			<div className="flex items-center gap-2 px-4 pt-2 text-xs text-muted-foreground" role="status">
				<Loader2Icon className="size-3 animate-spin" />
				Transcribing…
			</div>
		);
	}

	if (!error) return null;

	return (
		<div className="flex items-center gap-2 px-4 pt-2 text-xs text-red-700" role="alert">
			<span className="flex-1">{error}</span>
			{failedRecording && (
				<Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => void retry(failedRecording)}>
					Retry
				</Button>
			)}
			<Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={clearError}>
				Dismiss
			</Button>
		</div>
	);
};
